import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { formatMoneyFromCents } from "../../../../helpers/money";
import usePlanFeatures from "../../../../hooks/usePlanFeatures";
import { getAuthRoleNames } from "../../../../services/authService";
import { getProducts } from "../../../../services/productService";
import { getServiceBundles } from "../../../../services/serviceBundleService";
import { getServices } from "../../../../services/serviceService";
import { Product } from "../../../../types/product";
import { Service } from "../../../../types/service";
import { ServiceBundle } from "../../../../types/serviceBundle";

type LookupTab = "services" | "bundles" | "products";

const PAGE_SIZE = 100;

const CounterLookup = () => {
  const { t, i18n } = useTranslation();
  const { can, isLoading: planLoading } = usePlanFeatures();
  const inventoryEnabled = can("inventory");
  const isReceptionist = useMemo(() => {
    const roles = getAuthRoleNames();

    return roles.includes("receptionist")
      && !roles.includes("owner")
      && !roles.includes("manager");
  }, []);

  const [tab, setTab] = useState<LookupTab>("services");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [bundles, setBundles] = useState<ServiceBundle[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [truncated, setTruncated] = useState(false);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const load = useCallback(async () => {
    if (tab === "products" && !inventoryEnabled) {
      setProducts([]);
      setTruncated(false);
      return;
    }

    setLoading(true);

    try {
      const params = {
        per_page: PAGE_SIZE,
        search: appliedSearch || undefined,
      };

      if (tab === "services") {
        const response = await getServices(params);
        setServices(response.data.filter((service) => service.is_active));
        setTruncated((response.meta?.total ?? response.data.length) > response.data.length);
      }

      if (tab === "bundles") {
        const response = await getServiceBundles({ ...params, is_active: true });
        setBundles(response.data);
        setTruncated((response.meta?.total ?? response.data.length) > response.data.length);
      }

      if (tab === "products") {
        const response = await getProducts(params);
        setProducts(response.data);
        setTruncated((response.meta?.total ?? response.data.length) > response.data.length);
      }
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("studio.counterLookup.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, inventoryEnabled, t, tab]);

  useEffect(() => {
    if (planLoading) {
      return;
    }

    load();
  }, [load, planLoading]);

  document.title = `${t("studio.counterLookup.title")} | Adatis`;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("studio.counterLookup.title")} pageTitle={t("menu.agenda")} />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-1">{t("studio.counterLookup.title")}</h4>
                <p className="text-muted mb-0">{t("studio.counterLookup.lead")}</p>
              </CardHeader>
              <CardBody>
                {isReceptionist ? (
                  <Alert color="info">
                    {t("studio.counterLookup.receptionistNotice")}
                  </Alert>
                ) : null}

                <Nav tabs className="nav-tabs-custom mb-3">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={tab === "services" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        setTab("services");
                      }}
                    >
                      {t("studio.counterLookup.tabs.services")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={tab === "bundles" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        setTab("bundles");
                      }}
                    >
                      {t("studio.counterLookup.tabs.bundles")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={tab === "products" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        setTab("products");
                      }}
                    >
                      {t("studio.counterLookup.tabs.products")}
                    </NavLink>
                  </NavItem>
                </Nav>

                <form
                  className="d-flex flex-wrap gap-2 mb-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setAppliedSearch(search.trim());
                  }}
                >
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("studio.counterLookup.searchPlaceholder")}
                    aria-label={t("studio.counterLookup.searchPlaceholder")}
                    className="w-auto flex-grow-1"
                  />
                  <Button color="primary" type="submit">
                    {t("studio.counterLookup.search")}
                  </Button>
                </form>

                {loading || planLoading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary">{t("studio.counterLookup.loading")}</Spinner>
                  </div>
                ) : null}

                {!loading && !planLoading && truncated ? (
                  <p className="text-muted small">{t("studio.counterLookup.truncated")}</p>
                ) : null}

                {!loading && !planLoading && tab === "services" ? (
                  services.length === 0 ? (
                    <p className="text-muted mb-0">{t("studio.counterLookup.empty.services")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.counterLookup.columns.name")}</th>
                            <th>{t("studio.counterLookup.columns.category")}</th>
                            <th>{t("studio.counterLookup.columns.duration")}</th>
                            <th>{t("studio.counterLookup.columns.organization")}</th>
                            <th>{t("studio.counterLookup.columns.price")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {services.map((service) => (
                            <tr key={service.id}>
                              <td>{service.name}</td>
                              <td>{service.category?.name || "—"}</td>
                              <td>{t("studio.counterLookup.minutes", { count: service.duration_minutes })}</td>
                              <td>
                                {service.buffer_minutes > 0
                                  ? t("studio.counterLookup.minutes", { count: service.buffer_minutes })
                                  : "—"}
                              </td>
                              <td>
                                {service.price_is_from
                                  ? t("studio.services.price.from", {
                                      price: formatMoney(service.price),
                                    })
                                  : formatMoney(service.price)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )
                ) : null}

                {!loading && !planLoading && tab === "bundles" ? (
                  bundles.length === 0 ? (
                    <p className="text-muted mb-0">{t("studio.counterLookup.empty.bundles")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.counterLookup.columns.name")}</th>
                            <th>{t("studio.counterLookup.columns.includes")}</th>
                            <th>{t("studio.counterLookup.columns.duration")}</th>
                            <th>{t("studio.counterLookup.columns.price")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bundles.map((bundle) => (
                            <tr key={bundle.id}>
                              <td>{bundle.name}</td>
                              <td>
                                {(bundle.items ?? [])
                                  .map((item) => item.service?.name)
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </td>
                              <td>{t("studio.counterLookup.minutes", { count: bundle.duration_minutes })}</td>
                              <td>
                                {bundle.price_is_from
                                  ? t("studio.services.price.from", {
                                      price: formatMoney(bundle.effective_price_cents),
                                    })
                                  : formatMoney(bundle.effective_price_cents)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )
                ) : null}

                {!loading && !planLoading && tab === "products" ? (
                  !inventoryEnabled ? (
                    <p className="text-muted mb-0">{t("studio.counterLookup.productsPlan")}</p>
                  ) : products.length === 0 ? (
                    <p className="text-muted mb-0">{t("studio.counterLookup.empty.products")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.counterLookup.columns.name")}</th>
                            <th>{t("studio.counterLookup.columns.category")}</th>
                            <th>{t("studio.counterLookup.columns.price")}</th>
                            <th>{t("studio.counterLookup.columns.stock")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product) => {
                            const lowStock = product.current_stock <= product.minimum_stock;

                            return (
                              <tr key={product.id}>
                                <td>{product.name}</td>
                                <td>{product.category?.name || "—"}</td>
                                <td>
                                  {product.sale_price !== null
                                    ? formatMoney(product.sale_price)
                                    : "—"}
                                </td>
                                <td>
                                  {product.current_stock}
                                  {lowStock ? (
                                    <Badge color="warning" className="ms-2">
                                      {t("studio.counterLookup.lowStock")}
                                    </Badge>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CounterLookup;
